import { QueryClient } from '@tanstack/react-query';

import { GetQuestionDetailWithNearSimplyQuestionsQuery } from '@/types/bff/gql-schema';
import { AnswererType, QnaDetailVo } from '@/types/qna';
import { displayedAt, getNearQuestion, sortImageBlindTypeFormat } from '@/utils/format';

import { useGetQuestionDetailWithNearSimplyQuestionsQuery } from './mobile/qna.gql.impl';

export const parseQnaDetailVo = (qnaDetail: GetQuestionDetailWithNearSimplyQuestionsQuery): QnaDetailVo | undefined => {
  if (!qnaDetail.questionV2) return undefined;
  return {
    ...qnaDetail.questionV2,
    nameAndDate: `${qnaDetail.questionV2.user.name} • ${displayedAt(qnaDetail.questionV2.createdAt)}`,
    answers: qnaDetail.questionV2.answers.map((answer) => {
      return {
        ...answer,
        createdDate: displayedAt(answer.createdAt),
        reviews: answer.reviews.map((review) => {
          return {
            ...review,
            content: review.content ?? '',
            createdAt: displayedAt(review.createdAt),
            updatedAt: displayedAt(review.updatedAt),
          };
        }),
        isReviewViewable: answer.answerer.answererType === AnswererType.Nurse,
        hasReview: answer.reviews.length > 0,
      };
    }),
    attachments: sortImageBlindTypeFormat(qnaDetail.questionV2.attachments),
    isNotAnswered: !qnaDetail.questionV2.answererCount,
    nearSimpleQuestions: getNearQuestion(qnaDetail.questionV2.questionPid, qnaDetail.nearSimpleQuestions),
    paymentTotal: qnaDetail.questionV2.paymentTotal,
    paymentInfo: qnaDetail.questionV2.paymentInfo,
  };
};

const SIZE = 3;
const useGetQnaDetailWithNearSimplyQuestions = (questionPid: string) => {
  const {
    data: qnaDetail,
    isLoading,
    refetch: refetchQnaDetail,
  } = useGetQuestionDetailWithNearSimplyQuestionsQuery(
    {
      questionPid,
      size: SIZE,
    },
    {
      select: parseQnaDetailVo,
    },
  );
  return {
    qnaDetail,
    isLoading,
    refetchQnaDetail,
  };
};

export default useGetQnaDetailWithNearSimplyQuestions;

useGetQnaDetailWithNearSimplyQuestions.getKey = (questionPid: string) => {
  return useGetQuestionDetailWithNearSimplyQuestionsQuery.getKey({ questionPid, size: SIZE });
};

useGetQnaDetailWithNearSimplyQuestions.fetchQuery = async (queryClient: QueryClient, questionPid: string) => {
  return await queryClient.fetchQuery(
    useGetQuestionDetailWithNearSimplyQuestionsQuery.getKey({ questionPid, size: SIZE }),
    useGetQuestionDetailWithNearSimplyQuestionsQuery.fetcher({
      questionPid,
      size: SIZE,
    }),
  );
};
